import {AppApi} from 'store/customer/api';
import {
    StripeConfirmResponse,
    StripeConfirmResponseRaw,
} from 'components/customer/Confirmation/Gateways/Stripe/entity/StripeConfirmation';
import {StripePaymentStatus} from 'components/customer/Confirmation/Gateways/Stripe/entity/StripePaymentStatus';
import {
    mapToPaymentOptions,
    PaymentOptions,
    PaymentOptionsApiResponse,
} from 'components/customer/Confirmation/Payment/entity/PaymentOptions';
import {GatewayCode} from 'components/manufacturer/Payments/entity/Gateway';
import {PaymentMethodType} from 'components/manufacturer/Payments/entity/Setting';
import {
    mapPaymentSession,
    PaymentSession,
    PaymentSessionApiResponse,
} from 'components/customer/Confirmation/Payment/entity/PaymentSession';
import {
    mapPaymentSummary,
    PaymentSummary,
    PaymentSummaryApiResponse,
} from 'components/customer/Confirmation/Payment/entity/PaymentSummary';
import {
    Invoice,
    InvoiceApiResponse,
    mapInvoice,
} from 'components/customer/Confirmation/Invoice/entity/Invoice';

const gatewayApi = AppApi.injectEndpoints({
    endpoints: (build) => ({
        fetchPaymentOptions: build.query<PaymentOptions, {jobID: number}>({
            query: ({jobID}) => `payment/job/${jobID}/payment-options`,
            transformResponse: (response: {
                data: PaymentOptionsApiResponse;
            }) => {
                return mapToPaymentOptions(response.data);
            },
            providesTags: ['PaymentOptions'],
        }),
        fetchStripeClientSecret: build.query<
            PaymentSession,
            {jobID: number; paymentType?: PaymentMethodType}
        >({
            query: ({jobID, paymentType = PaymentMethodType.CARD_FULL}) => ({
                url: 'payment/session',
                method: 'POST',
                body: {
                    job_id: jobID,
                    gateway: GatewayCode.STRIPE,
                    payment_method_type: 'card_domestic',
                    payment_type:
                        paymentType == PaymentMethodType.CARD_FULL
                            ? 'full'
                            : 'deposit', // possibly update this to actually match the paymentmethodtype
                },
            }),
            transformResponse: (response: {
                data: PaymentSessionApiResponse;
            }) => {
                return mapPaymentSession(response.data);
            },
            providesTags: (result, error, {jobID, paymentType}) => {
                return [
                    {type: 'PaymentSession', id: jobID},
                    {type: 'PaymentSession', id: paymentType},
                ];
            },
        }),
        fetchStripePaymentSummary: build.query<
            PaymentSummary,
            {
                confirmationToken: string;
                paymentIntentId: string;
                manufacturerId: number;
                paymentMethod?: PaymentMethodType;
                jobId: number;
            }
        >({
            query: ({
                confirmationToken,
                paymentIntentId,
                manufacturerId,
                paymentMethod = PaymentMethodType.CARD_FULL,
                jobId,
            }) => {
                return {
                    url: 'payment/session/update',
                    method: 'POST',
                    body: {
                        jobId,
                        payment_session_id: paymentIntentId,
                        confirmation_token_id: confirmationToken,
                        gateway: GatewayCode.STRIPE,
                        manufacturer_id: manufacturerId,
                        payment_type:
                            paymentMethod == PaymentMethodType.CARD_FULL
                                ? 'full'
                                : 'deposit', // possibly update this to actually match the paymentmethodtype
                    },
                };
            },
            transformResponse: ({data}: {data: PaymentSummaryApiResponse}) =>
                mapPaymentSummary(data),
        }),
        confirmPayment: build.query<
            StripeConfirmResponse,
            {
                manufacturerId: number;
                confirmationTokenId: string;
                paymentIntentId: string;
                jobId: number;
            }
        >({
            query: ({
                manufacturerId,
                confirmationTokenId,
                paymentIntentId,
                jobId,
            }) => ({
                url: 'payment/session/confirm',
                method: 'POST',
                body: {
                    jobId,
                    manufacturer_id: manufacturerId,
                    gateway: GatewayCode.STRIPE,
                    confirmation_token_id: confirmationTokenId,
                    payment_session_id: paymentIntentId,
                },
            }),
            transformResponse: ({data}: {data: StripeConfirmResponseRaw}) => ({
                redirectUrl: data.redirect_url,
                requiresAction: data.requires_action,
                clientSecret: data.payment_intent_client_secret,
                actionType: data.action_type,
                qrCodeData: data.qr_code_data,
                qrCodeImagePNG: data.qr_code_image_url_png,
                qrCodeImageSVG: data.qr_code_image_url_svg,
                qrCodeHostedInstructionsURL: data.hosted_instructions_url,
            }),
        }),
        fetchStripeStatus: build.query<
            {
                status: StripePaymentStatus;
            },
            {
                intentId: string;
                manufacturerId: number;
                gateway?: GatewayCode;
                jobId: number;
            }
        >({
            query: ({
                intentId,
                manufacturerId,
                gateway = GatewayCode.STRIPE,
                jobId,
            }) => {
                return {
                    url: `payment/session/${intentId}/status?gateway=${gateway}&manufacturer_id=${manufacturerId}?jobId=${jobId}`,
                    method: 'GET',
                };
            },
            transformResponse: ({
                data,
            }: {
                data: {
                    status: StripePaymentStatus;
                };
            }) => ({
                status: data.status,
            }),
        }),
        accountPayment: build.mutation<void, {jobId: number}>({
            query: ({jobId}) => {
                return {
                    url: 'payment/account',
                    method: 'POST',
                    body: {
                        job_id: jobId,
                    },
                };
            },
        }),
        invoicePayment: build.mutation<
            void,
            {jobId: number; paymentMethod: PaymentMethodType}
        >({
            query: ({jobId, paymentMethod}) => {
                return {
                    url: 'payment/invoice',
                    method: 'POST',
                    body: {
                        job_id: jobId,
                        payment_type:
                            paymentMethod == PaymentMethodType.INVOICE_DEPOSIT
                                ? 'deposit'
                                : 'full',
                    },
                };
            },
        }),
        fetchInvoiceDetails: build.query<Invoice, {jobId: number}>({
            query: ({jobId}) => `payment/invoice/${jobId}/details`,
            transformResponse: (response: {data: InvoiceApiResponse}) => {
                return mapInvoice(response.data);
            },
            providesTags: ['InvoiceDetails'],
        }),
        invoicePaymentConfirmation: build.mutation<void, {jobId: number}>({
            query: ({jobId}) => ({
                url: `payment/invoice/${jobId}/notify-bank-transfer`,
                method: 'POST',
            }),
        }),
    }),
});

export const {
    useLazyFetchStripeClientSecretQuery,
    useFetchStripeClientSecretQuery,
    useFetchStripeStatusQuery,
    useLazyFetchStripePaymentSummaryQuery,
    useLazyConfirmPaymentQuery,
    useFetchPaymentOptionsQuery,
    useInvoicePaymentMutation,
    useAccountPaymentMutation,
    useFetchInvoiceDetailsQuery,
    useInvoicePaymentConfirmationMutation,
    useLazyFetchPaymentOptionsQuery,
} = gatewayApi;
